// Prüfung von Dateien vor dem Hochladen (UploadDropzone, DESIGN §3.4.8): Typ und Größe.

export interface FileRules {
  /** Erlaubte Endungen (".pdf") oder MIME-Typen ("image/*", "text/csv"); leer = alles. */
  accept?: string[];
  /** Höchstgröße in Bytes. */
  maxSize?: number;
}

export interface FileCheck {
  accepted: File[];
  errors: string[];
}

const UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/** 1536 → „1.5 KB“, 26214400 → „25 MB“ (Basis 1024, höchstens eine Nachkommastelle). */
export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} ${UNITS[unit]}`;
}

export function matchesAccept(
  file: Pick<File, 'name' | 'type'>,
  accept: string[] | undefined,
): boolean {
  if (!accept || accept.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return accept.some((rule) => {
    const r = rule.trim().toLowerCase();
    if (r.startsWith('.')) return name.endsWith(r);
    if (r.endsWith('/*')) return type.startsWith(r.slice(0, -1));
    return type === r;
  });
}

/** Lesbare Typen für den Hinweis: [".pdf", ".docx", "image/*"] → „PDF, DOCX, images“, ["image/*"] → „Images“. */
export function describeAccept(accept: string[] | undefined): string {
  if (!accept || accept.length === 0) return 'Any file type';
  const text = accept
    .map((rule) =>
      rule.startsWith('.')
        ? rule.slice(1).toUpperCase()
        : rule.endsWith('/*')
          ? `${rule.slice(0, -2)}s`
          : rule,
    )
    .join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function checkFiles(files: File[], rules: FileRules, multiple = true): FileCheck {
  const errors: string[] = [];
  const accepted: File[] = [];
  const list = multiple ? files : files.slice(0, 1);
  if (!multiple && files.length > 1) errors.push('Drop one file at a time.');
  for (const file of list) {
    if (!matchesAccept(file, rules.accept))
      errors.push(`${file.name}: this file type isn't supported.`);
    else if (rules.maxSize !== undefined && file.size > rules.maxSize)
      errors.push(`${file.name} is larger than ${formatBytes(rules.maxSize)}.`);
    else accepted.push(file);
  }
  return { accepted, errors };
}

/** Hat ein Drag-Ereignis Dateien dabei (nicht Text oder Links)? */
export const dragHasFiles = (types: readonly string[] | DOMStringList | undefined): boolean =>
  Boolean(types && Array.from(types as ArrayLike<string>).includes('Files'));
